<template>
  <div class="page-ct">
    <div class="page" v-loading="isLoading">
      <page-header-new :title="$t('Dashboard_Title')" class="page-header">
        <template v-slot:actions>
          <div class="old-dash-link">
            <a :href="`index-old?project_id=${$store.getters.projectId}`">
              {{ $t('Dashboard_SwitchToOldDashboard') }}
            </a>
          </div>
          <channel-select
            :platform="true"
            @change="fetchData"
            :class="{ 'hide-channel-select': !hasData() }"
          />
          <date-filter
            @change="handlerDateFilter($event)"
            :class="{ 'hide-picker': !hasData() }"
          />
        </template>
      </page-header-new>

      <div class="welcome-ct" v-if="!isLoading && !hasData()">
        <h3>{{ $t('Dashboard_Welcome') }}</h3>
        <p class="welcome-msg" v-html="$t('Dashboard_WelcomeMessage')" />
      </div>

      <required-actions-banner v-if="$can('viewProjectItem')" />
      <div class="key-metrics" v-if="data.addressable_users.length > 0">
        <h3>{{ $t('Dashboard_AddressableUsers') }}</h3>
        <div class="metrics-ct">
          <div v-for="keyMetric in data.addressable_users" class="metric">
            <key-metric
              :metric="keyMetric.value"
              :type="keyMetric.type"
              :title="
                channelLabels.hasOwnProperty(keyMetric.name)
                  ? $t(channelLabels[keyMetric.name])
                  : keyMetric.name
              "
            />
          </div>
        </div>
      </div>

      <div class="key-metrics" v-if="!hasData()">
        <h3>{{ $t('Dashboard_KeyMetrics') }}</h3>
        <div class="metrics-ct">
          <div v-for="index in 3" :key="index" class="metric">
            <key-metric-placeholder />
          </div>
        </div>
      </div>

      <div class="channels">
        <h3>{{ $t('Dashboard_Channels') }}</h3>
        <el-tag effect="plain" v-if="filteredChannels" size="mini">
          {{
            $t('Dashboard_FilteredChannels', {
              number: filteredChannels
            })
          }}
        </el-tag>
        <div class="items-ct">
          <template v-if="data.channels.length > 0">
            <channel-overview
              v-for="(channel, index) in data.channels"
              :key="index"
              :metrics="channel.metrics"
              :channel-name="channel.name"
              class="item"
            />
          </template>
          <template v-if="!hasData()">
            <project-overview-placeholder v-for="index in 2" :key="index" />
          </template>
        </div>
      </div>

      <div
        class="key-metrics"
        v-if="data.project.length > 0"
        v-for="section in data.project"
      >
        <template v-if="section.name === 'mobile'">
          <h3>{{ $t(keyMetricLabels[section.name]) }}</h3>
          <div class="metrics-ct">
            <div v-for="keyMetric in section.metrics" class="metric">
              <key-metric
                :metric="keyMetric.value"
                :type="keyMetric.type"
                :title="
                  metricLabels.hasOwnProperty(keyMetric.name)
                    ? $t(metricLabels[keyMetric.name])
                    : keyMetric.name
                "
              />
            </div>
          </div>
        </template>
      </div>

      <div class="events" v-if="data.events.length > 0">
        <h3>{{ $t('Dashboard_TopEventHits') }}</h3>
        <div class="events-ct">
          <event-panel
            v-for="(event, index) in data.events"
            :key="index"
            :title="eventMetricLabels[event.name]"
            :events="event.metrics"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import DateFilter from '@/common/components/dashboard/DateFilter'
import ChannelOverview from '@/common/components/ChannelOverview'
import RestApi from '@/core/services/RestApi'
import moment from 'moment'
import KeyMetric from '@/common/components/KeyMetric'
import EventPanel from '@/common/components/dashboard/EventPanel'
import { getDateFilterRange, projectEndpoint } from '@/core/helpers'
import {
  keyMetricLabels,
  metricLabels,
  channelLabels,
  eventMetricLabels
} from '@/common/components/dashboard/Labels'
import ProjectOverviewPlaceholder from '@/common/components/ProjectOverviewPlaceholder'
import KeyMetricPlaceholder from '@/common/components/KeyMetricPlaceholder'
import ChannelSelect from '@/common/components/analytics/ChannelSelect'
import { mapGetters } from 'vuex'
import RequiredActionsBanner from '@/modules/dashboard/components/RequiredActionsBanner'

export default {
  name: 'ProjectDashboard',

  components: {
    RequiredActionsBanner,
    ChannelSelect,
    KeyMetricPlaceholder,
    ProjectOverviewPlaceholder,
    PageHeaderNew,
    EventPanel,
    KeyMetric,
    DateFilter,
    ChannelOverview
  },

  data() {
    return {
      filteredChannels: 0,
      date_from: null,
      date_to: null,
      data: {
        project: [],
        channels: [],
        events: [],
        addressable_users: []
      },
      keyMetricLabels,
      metricLabels,
      channelLabels,
      eventMetricLabels,
      isLoading: true
    }
  },

  computed: {
    ...mapGetters(['userConfig'])
  },
  created() {
    let dateRange = getDateFilterRange()
    this.handlerDateFilter(dateRange)
  },
  methods: {
    handlerDateFilter(dateRange) {
      this.date_from = moment(dateRange.start).format('YYYY-MM-DD')
      this.date_to = moment(dateRange.end).format('YYYY-MM-DD')
      this.fetchData()
    },
    fetchData() {
      this.isLoading = true

      RestApi(
        projectEndpoint({
          method: 'get',
          url: `analytics`,
          params: {
            platforms: this.userConfig.show_channel_platforms,
            date_from: this.date_from,
            date_to: this.date_to
          }
        })
      )
        .then(({ data }) => {
          Object.assign(this.data, data)
          this.filteredChannels = 0
          if (this.userConfig.channels && data.channels.length > 0) {
            let availableChannels = Object.keys(this.userConfig.channels)

            this.data.channels = data.channels.filter(channel => {
              let name = this.userConfig.show_channel_platforms
                ? availableChannels.filter(ch => channel.name.includes(ch))[0]
                : channel.name
              if (name && this.userConfig.channels[name]) {
                return true
              }
              this.filteredChannels += 1
            })
          }
          this.isLoading = false
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
          if (error.response.status === 403) {
            this.$router.push({ name: 'Home' })
          }
        })
    },

    hasData() {
      return (
        this.filteredChannels ||
        this.data.project.length > 0 ||
        this.data.channels.length > 0 ||
        this.data.events.length > 0 ||
        this.data.addressable_users.length > 0
      )
    }
  }
}
</script>
<style scoped lang="scss">
.page-ct {
  width: 100%;
  overflow: auto;
  background-color: #f2f5f9;
}

.page {
  width: 100%;
  background-color: #f2f5f9;
  overflow: auto;

  .hide-picker,
  .hide-channel-select {
    visibility: hidden;
  }

  > div {
    margin: 0 auto 28px auto;
    padding-left: 20px;
    padding-right: 20px;
    width: 1092px;
  }

  .old-dash-link {
    margin-bottom: 0;
    margin-right: 20px;

    a {
      color: #1d87c8;
    }

    a:hover {
      text-decoration: underline;
    }
  }

  .page-header {
    margin-left: 0;
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
    width: 100%;
  }

  .header-toolbar {
    margin: 2px 20px 10px 20px;
    display: flex;
    justify-content: flex-end;
  }

  h3 {
    margin-bottom: 10px;
  }

  .welcome-ct {
    margin-bottom: 28px;

    .welcome-msg {
      width: 536px;
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 22px;
      color: #808080;
    }
  }

  .channels {
    h3 {
      display: inline-block;
    }
    .items-ct {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: space-between;

      .item {
        margin-bottom: 20px;
      }
    }
  }

  .key-metrics {
    .metrics-ct {
      display: flex;
      flex-direction: row;

      .metric > div {
        width: 260px;
        margin-right: 13px;
      }
    }
  }

  .events {
    .events-ct {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: space-between;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/dashboard/pages/ProjectDashboard.vue